// src/merch/JerseyPanel.jsx
//
// "Jersey" card for products with recolourable art (the ornament): turn
// the painted player on, pick a colour combo, or set each part's colour.
// Applies to every side at once (front + back stay matched).

import React, { useEffect, useRef, useState } from "react";
import { useSnapshot } from "valtio";
import { merch } from "./store";
import { PRESETS, PLAIN_PRESETS, REGION_LABELS, OFF_TARGET, MASKS, VISOR_TINTS, CAGE_COLOURS, HAIRS, HAIR_COLOURS, applyJersey, currentJersey, loadTemplateMeta, resolveColour } from "./jersey";

// optional trims: off until someone picks a colour
const OFF_UNTIL_SET = new Set(["stripe-trim", "stripe-edge", "stripe-inner"]);

// picking a colour combo keeps the face mask / visor tint / cage / hair
const keepExtras = (c) => {
  const out = {};
  for (const k of ["_mask", "_visorTint", "cage", "_hair", "hair"]) if (c?.[k] !== undefined) out[k] = c[k];
  return out;
};

export default function JerseyPanel({ product }) {
  const snap = useSnapshot(merch);
  const colours = currentJersey(snap);
  const [regions, setRegions] = useState([]);
  const [styles, setStyles] = useState([]); // stripe layouts beyond the classic one
  const [masks, setMasks] = useState([]);   // face masks available in the art
  const [hairs, setHairs] = useState([]);   // hair styles available in the art
  const [draft, setDraft] = useState(null); // shows picks instantly; applied debounced
  const [busy, setBusy] = useState(false);
  const [details, setDetails] = useState(false); // "Jersey details" drill-in
  const timer = useRef(null);

  useEffect(() => {
    let alive = true;
    loadTemplateMeta(product.template.base)
      .then((m) => {
        const set = new Set();
        Object.values(m.sides).forEach((s) => s.regions.forEach((r) => r !== "fixed" && set.add(r)));
        const order = Object.keys(REGION_LABELS);
        if (alive) setRegions(order.filter((r) => set.has(r)));
        const st = new Set();
        Object.values(m.sides).forEach((s) => (s.styles || []).forEach((x) => st.add(x)));
        if (alive) setStyles([...st]);
        const ov = new Set();
        Object.values(m.sides).forEach((s) => Object.keys(s.overlays || {}).forEach((x) => ov.add(x)));
        if (alive) setMasks(MASKS.filter((x) => ov.has(x)));
        if (alive) setHairs(HAIRS.filter((x) => ov.has(x)));
      })
      .catch((e) => console.warn("[jersey] template missing", e));
    return () => { alive = false; clearTimeout(timer.current); };
  }, [product.template.base]);

  useEffect(() => { setDraft(null); }, [JSON.stringify(colours)]); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = draft || colours;

  async function run(c) {
    setBusy(true);
    try { await applyJersey(c); } finally { setBusy(false); }
  }
  function pick(region, value) {
    const next = { ...(shown || PRESETS[0].c), [region]: value };
    setDraft(next);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => run(next), 150);
  }

  return (
    <div className="ma-jersey">
      <div className="ma-jersey-head">
        <span>👕 <b>Jersey</b>{busy && <span className="ma-muted"> · colouring…</span>}</span>
        {colours ? (
          <button className="ma-link" onClick={() => run(null)}>Remove</button>
        ) : (
          <button className="ma-btn primary small" onClick={() => run(PRESETS[0].c)}>Add jersey</button>
        )}
      </div>
      {!colours && (
        <p className="ma-muted">A painted player to colour in your team's colours. Add your face, name and number on top.</p>
      )}
      {colours && (
        <>
          {(() => {
            const hasBand = regions.includes("stripe-2");
            const bandOff = typeof shown?.["stripe-2"] === "string" && shown["stripe-2"].startsWith("=");
            const list = hasBand && bandOff ? PLAIN_PRESETS : PRESETS;
            const same = (a, b) => (a || "").toLowerCase() === (b || "").toLowerCase();
            const isOn = (p) => ["jersey", "stripe-1", "pants", "helmet"].every((r) => same(resolveColour(p.c, r), resolveColour(shown, r)));
            return (
              <>
                <div className="ma-field-row"><h3>Colour template</h3><span className="ma-hint">{list.length} combos</span></div>
                {hasBand && (
                  <div className="ma-seg full small ma-band-seg" role="group" aria-label="Chest band">
                    <button aria-pressed={!bandOff} onClick={() => run({ ...shown, "stripe-2": (PRESETS.find(isOn) || PRESETS[0]).c["stripe-2"], "band-trim": undefined })}>With chest band</button>
                    <button aria-pressed={bandOff} onClick={() => run({ ...shown, "stripe-2": "=jersey", "band-trim": "=jersey" })}>No chest band</button>
                  </div>
                )}
                <div className="ma-palette-grid" role="group" aria-label="Colour templates">
                  {list.map((p) => {
                    const on = isOn(p);
                    const cols = ["jersey", "stripe-1", ...(list === PRESETS && hasBand ? ["stripe-2"] : []), "pants"].map((r) => resolveColour(p.c, r));
                    return (
                      <button key={p.name} className={`ma-palette ${on ? "selected" : ""}`} aria-pressed={on} title={p.name} onClick={() => run({ ...p.c, ...keepExtras(shown) })}>
                        <span className="ma-palette-strip">{cols.map((c, i) => <i key={i} style={{ background: c }} />)}</span>
                        <span className="ma-palette-name">{p.name.replace(/, no chest band$/, "").replace(" (as painted)", "")}</span>
                        {on && <span className="ma-palette-check"><svg viewBox="0 0 24 24" width="10" height="10" aria-hidden="true"><path d="m5 12 4 4L19 6" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" /></svg></span>}
                      </button>
                    );
                  })}
                </div>
              </>
            );
          })()}
          {styles.length > 0 && (
            <>
              <h3>Stripe style</h3>
              <div className="ma-seg full small" role="group" aria-label="Stripe layout">
                {[null, ...styles].map((st) => (
                  <button
                    key={st || "classic"}
                    aria-pressed={(shown?._style || null) === st}
                    onClick={() => run({ ...(shown || PRESETS[0].c), _style: st || undefined })}
                  >
                    {st ? st.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "Classic"}
                  </button>
                ))}
              </div>
            </>
          )}
          <button className={`ma-detail-row ${details ? "open" : ""}`} aria-expanded={details} onClick={() => setDetails((v) => !v)}>
            <span className="ma-detail-icon"><svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true"><g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3-9 5 9 5 9-5-9-5Z" /><path d="m3 12 9 5 9-5M3 16l9 5 9-5" /></g></svg></span>
            <span><strong>Jersey details</strong><small>{[masks.length && "Face mask", hairs.length && "hair", "every colour"].filter(Boolean).join(", ")}</small></span>
            <svg className="ma-detail-arrow" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="m9 18 6-6-6-6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
          {details && <div className="ma-details">
          {masks.length > 0 && (
            <>
              <div className="ma-jersey-style">
                <span>Face mask</span>
                <div className="ma-seg small" role="group" aria-label="Face mask">
                  {[null, ...masks].map((mk) => (
                    <button
                      key={mk || "none"}
                      aria-pressed={(shown?._mask || null) === mk}
                      onClick={() => run({ ...(shown || PRESETS[0].c), _mask: mk || undefined })}
                    >
                      {mk ? mk[0].toUpperCase() + mk.slice(1) : "None"}
                    </button>
                  ))}
                </div>
              </div>
              {shown?._mask === "visor" && (
                <div className="ma-jersey-style">
                  <span>Tint</span>
                  <div className="ma-seg small" role="group" aria-label="Visor tint">
                    {Object.keys(VISOR_TINTS).map((t) => (
                      <button key={t} aria-pressed={(shown._visorTint || "clear") === t} onClick={() => run({ ...shown, _visorTint: t })}>
                        {t[0].toUpperCase() + t.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {shown?._mask === "cage" && (
                <div className="ma-jersey-style">
                  <span>Cage</span>
                  <div className="ma-jersey-presets ma-cage-cols" role="group" aria-label="Cage colour">
                    {CAGE_COLOURS.map(([n, c]) => (
                      <button key={n} title={n} aria-label={n} aria-pressed={(shown.cage || "#f4f4f0") === c} onClick={() => run({ ...shown, cage: c })}>
                        <i style={{ background: c }} />
                      </button>
                    ))}
                    <button title="Match helmet" aria-label="Match helmet" aria-pressed={shown.cage === "=helmet"} onClick={() => run({ ...shown, cage: "=helmet" })}>
                      <i style={{ background: resolveColour(shown, "helmet") }} /><small>helmet</small>
                    </button>
                    <input type="color" aria-label="Custom cage colour"
                      value={shown.cage && !shown.cage.startsWith("=") ? shown.cage : "#f4f4f0"}
                      onChange={(e) => pick("cage", e.target.value)} />
                  </div>
                </div>
              )}
            </>
          )}
          {hairs.length > 0 && (
            <>
              <div className="ma-jersey-style">
                <span>Hair</span>
                <div className="ma-seg small" role="group" aria-label="Hair">
                  {[null, ...hairs].map((hs) => (
                    <button
                      key={hs || "none"}
                      aria-pressed={(shown?._hair || null) === hs}
                      onClick={() => run({ ...(shown || PRESETS[0].c), _hair: hs || undefined })}
                    >
                      {hs ? hs[0].toUpperCase() + hs.slice(1) : "None"}
                    </button>
                  ))}
                </div>
              </div>
              {shown?._hair && (
                <div className="ma-jersey-style">
                  <span>Colour</span>
                  <div className="ma-jersey-presets ma-cage-cols" role="group" aria-label="Hair colour">
                    {HAIR_COLOURS.map(([n, c]) => (
                      <button key={n} title={n} aria-label={n} aria-pressed={(shown.hair || "#5b3a22") === c} onClick={() => run({ ...shown, hair: c })}>
                        <i style={{ background: c }} />
                      </button>
                    ))}
                    <input type="color" aria-label="Custom hair colour" value={shown.hair || "#5b3a22"} onChange={(e) => pick("hair", e.target.value)} />
                  </div>
                </div>
              )}
            </>
          )}
          <div className="ma-jersey-colours">
            {regions.map((r) => {
              const off = (typeof shown?.[r] === "string" && shown[r].startsWith("=")) || (shown?.[r] == null && OFF_UNTIL_SET.has(r));
              return (
                <span key={r} className={`ma-jersey-col ${off ? "is-off" : ""}`}>
                  <label title={off ? `${REGION_LABELS[r]}: off (pick a colour to turn it on)` : `${REGION_LABELS[r]} colour`}>
                    <input type="color" value={resolveColour(shown, r) || "#dcdcdc"} onChange={(e) => pick(r, e.target.value)} />
                    <span>{REGION_LABELS[r]}</span>
                  </label>
                  {OFF_TARGET[r] && !off && (
                    <button className="ma-jersey-off" title={`Turn ${REGION_LABELS[r].toLowerCase()} off`} onClick={() => pick(r, `=${OFF_TARGET[r]}`)}>off</button>
                  )}
                </span>
              );
            })}
          </div>
          </div>}
        </>
      )}
    </div>
  );
}
